import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import handleError from "../../../constants/handleError";
import InvoiceTemp from "../estimation/InvoiceTemp";
import DeleteModal from "../../../constants/DeleteModal";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import { useLocation, useNavigate } from "react-router-dom";
import Table from "../../../constants/Table";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";
import { ShowIcon } from "../../../constants/Icons";

const ReturnSaleList = ({ customerId, isActive }) => {
  const { isUser } = useAuth();
  const hasFetched = useRef(false);
  const [isEstimateData, setEstimateData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [currentEstimatePage, setCurrentEstimatePage] = useState(0);
  const [totalEstimatePages, setTotalEstimatePages] = useState(0);
  const [totalEstimateItems, setTotalEstimateItems] = useState(0);
  const { permission, hasPermission } = usePermission();

  const perPage = 10;
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const navigate = useNavigate();
  const location = useLocation();

  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [selectedReturnOption, setSelectedReturnOption] = useState("RETURN");

  const handleListEstimate = async (customerId, page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const response = await AxiosInstance.get(
        `module/salereturn/customer/${customerId}?pageno=${
          page + 1
        }&limit=${perPage}`,
      );
      const results = response.data.results || [];
      setEstimateData(results);
      if (results.length === 0) {
        setTotalEstimatePages(0);
        setTotalEstimateItems(0);
        setCurrentEstimatePage(0);
      } else {
        setTotalEstimatePages(response.data.totalPages || 0);
        setTotalEstimateItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (item) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:view") ||
      permission === undefined
    ) {
      try {
        const response = await AxiosInstance.get(
          `module/salereturn/${item._id}`,
        );
        setViewData(response.data.results);
        setViewModalOpen(true);
      } catch (error) {
        console.error("Error fetching setting data:", error);
        showAlert("error", "Failed to fetch setting data");
      }
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
    // Clear the location state to prevent re-opening the modal
    navigate(location.pathname, { replace: true, state: {} });
  };

  const openDeleteModal = (cItem) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:delete") ||
      permission === undefined
    ) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleReturnClose = () => {
    setViewModalOpen(false);
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.delete(
        `module/estimate/${isDeleteData?._id}`,
      );
      showAlert("success", Messages.SUCCESS.ESTIMATE_DELETED);

      const newTotal = totalEstimateItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isEstimateData.length === 1 && currentEstimatePage > 0) {
        setCurrentEstimatePage((prev) => prev - 1);
        await handleListEstimate(customerId, currentEstimatePage - 1);
      } else {
        await handleListEstimate(customerId, currentEstimatePage);
      }

      setTotalEstimateItems(newTotal);
      setTotalEstimatePages(newTotalPages);
      setDeleteModalOpen(false);

      closeDeleteModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteData(null);
    setDeleteModalOpen(false);
  };

  const getBgColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-honey";
      case "invoice":
        return "bg-label-moon";
      default:
        return "bg-label-secondary";
    }
  };

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const getBgTxtColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-wave";
      case "invoice":
        return "bg-label-deep-purple";
      default:
        return "bg-label-secondary";
    }
  };

  const getColor = (saleTrack) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "bg-label-success"; // Green
      case "UPDATED":
        return "bg-label-warning"; // Yellow
      case "DELETED":
        return "bg-label-danger"; // Red
      case "RETURNED":
        return "bg-label-orange";
      default:
        return "bg-label-secondary"; // Default color
    }
  };

  const getStatusLabel = (saleTrack) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "Original";
      case "UPDATED":
        return "Modified";
      case "DELETED":
        return "Cancelled";
      case "RETURNED":
        return "Returned";
      default:
        return "Unknown";
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (customerId) {
      handleListEstimate(customerId, currentEstimatePage);
    }
  }, [customerId]);

  return (
    <div>
      <Table
        loading={isLoading}
        data={isEstimateData}
        onRowClick={(item) => handleView(item)}
        columns={[
          {
            label: "S.No",
            render: (_, index) => (
              <>{currentEstimatePage * perPage + index + 1}</>
            ),
          },
          {
            label: "Sale Mode",
            render: (item) => (
              <span
                className={`badge rounded-pill ${getBgColor(item?.salemode)}`}
              >
                {formatTrackMode(item?.salemode)}
              </span>
            ),
          },
          {
            label: "Return No",
            render: (item) => (
              <span
                className={`badge rounded-pill ${getBgTxtColor(
                  item?.salemode,
                )}`}
              >
                {item?.saleReturnNo}
              </span>
            ),
          },
          {
            label: "Return Date",
            render: (item) => (
              <span className="text-heading fw-medium text-truncate">
                {
                  DateConverter.ISOToDisplayFormat(
                    DateConverter.convertToTimeZone(
                      item?.saleReturnDate,
                      isUser?.timezone,
                    ),
                  ).date
                }
              </span>
            ),
          },
          {
            label: "Return Items",
            render: (item) => <>{item?.totalReturnItemCount || 0} Item(s)</>,
          },
          {
            label: "Grand Total",
            render: (item) => (
              <span className="text-heading fw-medium text-truncate text-right p-2">
                {formatCurrencyFun(item?.returnGrandTotalRounded)}
              </span>
            ),
          },
          {
            label: "Status",
            thClass:"w-12",
            render: (item) => (
              <div className="no-print position-relative">
                <div className=" d-flex justify-content-center align-items-center">
              <span
                className={`stock-status-badge badge rounded-pill ${getColor(item.returnTrack)}`}
              >
                {getStatusLabel(item.returnTrack)}
              </span>
              <div className="order-actions order-actions3 opacity-0 opacity-md-100">
                <button
                  type="button"
                  className="btn-label-info shadow-none me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(item);
                  }}
                >
                  <ShowIcon/>
                </button>
              </div>
              </div>
              </div>
            ),
          },
          // {
          //   label: "Actions",
          //   render: (item) => (
          //     <div className="order-actions opacity-0 opacity-md-100">
          //       <button
          //         type="button"
          //         className="btn-label-info shadow-none me-2"
          //         onClick={(e) => {
          //           e.stopPropagation();
          //           handleView(item);
          //         }}
          //       >
          //         <i className="bx bx-show"></i>
          //       </button>
          //       {/* {item?.status === "DRAFT" && (
          //         <>
          //           <button
          //             type="button"
          //             className="btn-label-primary shadow-none me-2"
          //             // onClick={(e) => {
          //             //   e.stopPropagation();
          //             //   handleEdit(item);
          //             // }}
          //           >
          //             <i className="ng ng-ng-edit-icon"></i>
          //           </button>
          //           <button
          //             type="button"
          //             className="btn-label-danger shadow-none me-2"
          //             onClick={(e) => {
          //               e.stopPropagation();
          //               openDeleteModal(item);
          //             }}
          //           >
          //             <i className="ng ng-ng-delete-icon"></i>
          //           </button>
          //         </>
          //       )} */}
          //     </div>
          //   ),
          // },
        ]}
      />
      {totalEstimatePages > 0 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            previousLabel={"← Previous"}
            nextLabel={"Next →"}
            breakLabel={"..."}
            onPageChange={({ selected }) => {
              setIsLoading(true);
              setCurrentEstimatePage(selected);
            }}
            pageRangeDisplayed={5}
            pageCount={totalEstimatePages}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            forcePage={currentEstimatePage}
          />
        </div>
      )}
       <TemplateModalLayout
          isOpen={isViewModalOpen}
          isData={isViewData}
          onClose={handleReturnClose}
          selectedOption={selectedReturnOption}
          setSelectedOption={setSelectedReturnOption}
          setSaleData={setViewData}
          isActive={isActive}
        />
   

      {isDeleteModalOpen && (
        <DeleteModal
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDelete}
          isLable={" Estimation " + isDeleteData?.estimateno}
          isBtnLoading={isBtnLoading}
        />
      )}
    </div>
  );
};

export default ReturnSaleList;
